// FacilityContext.jsx

import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { getDebouncedFieldUpdate, markAsPending, clearPending, isPending } from '../../../utils/debouncedApiCalls'; // Removed createSafetyTimeout as it's not used
import { AuthContext } from '../../../contexts/AuthContext';
import { createEmptyFacility } from '../validationSchemas';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { facilityService } from '../../../services/facilityService';
import { useGroupCore } from './GroupCoreContext';
import { transformFacilityForApi } from '../transformers/facility-transformer';

// Detect test environment for better test compatibility
const isTestEnvironment = () => process.env.NODE_ENV === 'test';

export const FacilityContext = createContext();

export const FacilityProvider = ({ children }) => {
  const { auth } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { groupId, existingGroupId, setErrors } = useGroupCore();
  
  // Local state for facilities, primarily driven by react-query's cache
  const [facilities, setFacilities] = useState([createEmptyFacility()]);
  const [previewFacilityIndex, setPreviewFacilityIndex] = useState(0);
  
  const [showDelFacModal, setShowDelFacModal] = useState(false);
  const [facToRemove, setFacToRemove] = useState(null);
  const [deletionInProgress, setDeletionInProgress] = useState(false);
  
  const { 
    mutate: createFacilityMutation, // Renamed for clarity
    isLoading: isCreatingFacility
  } = useMutation({
    mutationFn: ({ groupId, data }) => facilityService.addFacility(groupId, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['facilities', 'group', variables.groupId]);
      queryClient.invalidateQueries(['group', variables.groupId]); // Consider if this is always needed
    },
    onError: (error, variables) => {
      console.error('Error creating facility:', error);
      setErrors(prev => ({ 
        ...prev, 
        facility_create: `Failed to create new facility for group ${variables.groupId}. Please try again.`
      }));
    }
  });
  
  // Mutation for updating a WHOLE facility (if ever needed, currently not directly used by field updates)
  const { 
    mutate: updateFullFacilityMutation, // Renamed for clarity
    isLoading: isUpdatingFullFacility 
  } = useMutation({
    mutationFn: ({ groupId, facilityId, data }) => facilityService.updateFacility(groupId, facilityId, data),
    onSuccess: (updatedFacilityData, variables) => {
      queryClient.setQueryData(['facilities', 'group', variables.groupId], (oldData) => {
        if (!oldData) return oldData;
        return oldData.map(facility =>
          facility.id === variables.facilityId ? { ...facility, ...(updatedFacilityData.data || updatedFacilityData) } : facility
        );
      });
      if (variables.facilityId) {
        queryClient.invalidateQueries(['facilities', 'facility', variables.facilityId]);
      }
    },
    onError: (error, variables) => {
        console.error(`Error updating facility ${variables.facilityId}:`, error);
        setErrors(prev => ({
            ...prev,
            [`facility_update_${variables.facilityId}`]: 'Failed to update facility details.'
        }));
    }
  });

  // NEW: Mutation for updating a single facility field optimistically
  const updateFacilityFieldMutation = useMutation({
    mutationFn: ({ groupId, facilityId, field, value }) => 
      facilityService.updateFacilityField(groupId, facilityId, field, value),
    onMutate: async (variables) => {
      const { groupId: currentGroupId, facilityId, field, value } = variables;
      const queryKey = ['facilities', 'group', currentGroupId];

      await queryClient.cancelQueries({ queryKey });
      const previousFacilities = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData) return [];
        return oldData.map(facility => {
          if (facility.id === facilityId || facility._id === facilityId) {
            const updatedFacility = { ...facility };
            // Handle nested fields
            if (field.includes('.')) {
              const parts = field.split('.');
              let current = updatedFacility;
              for (let i = 0; i < parts.length - 1; i++) {
                if (!current[parts[i]]) current[parts[i]] = {}; // Create path if not exists
                current = current[parts[i]];
              }
              current[parts[parts.length - 1]] = value;
            } else {
              updatedFacility[field] = value;
            }
            return updatedFacility;
          }
          return facility;
        });
      });
      return { previousFacilities, queryKey };
    },
    onError: (err, variables, context) => {
      console.error(`Error updating facility field ${variables.field}:`, err);
      if (context?.previousFacilities) {
        queryClient.setQueryData(context.queryKey, context.previousFacilities);
      }
      setErrors(prev => ({
        ...prev,
        [`facility_${variables.facilityIndex}_${variables.field}`]: `Failed to update ${variables.field.replace('.', ' ')}.`
      }));
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries(['facilities', 'group', variables.groupId]);
      if (variables.facilityId) {
         queryClient.invalidateQueries(['facilities', 'facility', variables.facilityId]);
      }
    },
  });
  
  const { 
    mutate: deleteFacilityMutation, // Renamed for clarity
    isLoading: isDeletingFacility
  } = useMutation({
    mutationFn: ({ groupId, facilityId }) => facilityService.removeFacility(groupId, facilityId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['facilities', 'group', variables.groupId]);
      queryClient.invalidateQueries(['group', variables.groupId]);
      setDeletionInProgress(false);
      setShowDelFacModal(false);
    },
    onError: (error) => {
      console.error('Error deleting facility:', error);
      setDeletionInProgress(false);
      setErrors(prev => ({ 
        ...prev, 
        facilityRemoval: 'Failed to remove facility from server.' 
      }));
    }
  });
  
  const { 
    isLoading: isFetchingFacilities,
    data: facilitiesDataFromQuery, // Renamed for clarity
    refetch: refetchFacilities
  } = useQuery({
    queryKey: ['facilities', 'group', existingGroupId || groupId],
    queryFn: async () => {
      const currentActiveGroupId = existingGroupId || groupId;
      if (!currentActiveGroupId) {
        console.log('No group ID available, skipping facilities fetch');
        return [];
      }
      console.log(`Fetching facilities for group: ${currentActiveGroupId}`);
      try {
        const response = await facilityService.getFacilities(currentActiveGroupId);
        return (response && response.data && Array.isArray(response.data)) ? response.data : (Array.isArray(response) ? response : []);
      } catch (error) {
        console.error('Error in facility query function:', error);
        throw error;
      }
    },
    enabled: !!(existingGroupId || groupId) && !!auth?.token,
    staleTime: 2 * 60 * 1000,
    onSuccess: (data) => {
      console.log('Facilities query succeeded with processed data:', data);
      if (data && Array.isArray(data)) {
        const processedFacilities = data.map(facility => ({
          ...createEmptyFacility(), // Ensure all fields from createEmptyFacility are present
          ...facility,
          id: facility.id || facility._id,
          _id: facility.id || facility._id,
          facilityName: facility.facilityName || facility.name || '', // Prefer facilityName
          name: facility.name || facility.facilityName || '', // Sync name
          // Ensure nested objects are initialized if not present
          contactPerson: facility.contactPerson || createEmptyFacility().contactPerson,
          facilityDetails: facility.facilityDetails || createEmptyFacility().facilityDetails,
          clinicalExpectations: facility.clinicalExpectations || createEmptyFacility().clinicalExpectations,
          listings: Array.isArray(facility.listings) ? facility.listings.map(l => ({ ...l, id: l.id || l._id })) : []
        }));
        setFacilities(processedFacilities.length ? processedFacilities : [createEmptyFacility()]);
      } else {
         setFacilities([createEmptyFacility()]);
      }
    },
    onError: (error) => {
      console.error('Error loading facilities:', error);
      setErrors(prev => ({ 
        ...prev, 
        facilities: 'Failed to load facilities. Please try refreshing the page.'
      }));
      setFacilities([createEmptyFacility()]);
    }
  });

  // Effect to update local `facilities` state when `facilitiesDataFromQuery` changes
  // This is the primary way `facilities` state gets updated from react-query
  useEffect(() => {
    if (facilitiesDataFromQuery) {
        const processedFacilities = facilitiesDataFromQuery.map(facility => ({
            ...createEmptyFacility(),
            ...facility,
            id: facility.id || facility._id,
            _id: facility.id || facility._id,
            facilityName: facility.facilityName || facility.name || '',
            name: facility.name || facility.facilityName || '',
            contactPerson: facility.contactPerson || createEmptyFacility().contactPerson,
            facilityDetails: facility.facilityDetails || createEmptyFacility().facilityDetails,
            clinicalExpectations: facility.clinicalExpectations || createEmptyFacility().clinicalExpectations,
            listings: Array.isArray(facility.listings) ? facility.listings.map(l => ({ ...l, id: l.id || l._id })) : []
        }));
        setFacilities(processedFacilities.length ? processedFacilities : [createEmptyFacility()]);
    } else if (!isFetchingFacilities) { // If not loading and no data, ensure initial empty state
        setFacilities([createEmptyFacility()]);
    }
  }, [facilitiesDataFromQuery, isFetchingFacilities]);

  const addFacility = useCallback(() => {
    const newFacility = createEmptyFacility();
    // Optimistically add to local UI for responsiveness before server confirmation
    // The query invalidation in createFacilityMutation will sync with server
    setFacilities(prev => [...prev, newFacility]); 
    setPreviewFacilityIndex(facilities.length); // Focus new facility

    if (existingGroupId) {
      const transformedFacility = transformFacilityForApi(newFacility);
      createFacilityMutation(
        { groupId: existingGroupId, data: transformedFacility },
        {
          // onSuccess for createFacilityMutation is handled globally by the hook
          // Specific onSuccess here if needed for local state adjustments post-server-response
          onSuccess: (response) => {
             // The main query invalidation will handle refetching and updating `facilitiesDataFromQuery`
             // which then updates `facilities` state via useEffect.
             // If immediate ID update is needed here before refetch, it can be done.
            console.log('Facility created on server, local state will sync via query refetch.', response);
          }
        }
      );
    }
  }, [existingGroupId, createFacilityMutation, setFacilities, facilities.length]);

  const updateFacilityField = useCallback(async (facilityIndex, field, value) => {
    // NO immediate local setFacilities to prevent focus loss.
    // UI updates will come from react-query's optimistic update.

    const groupIdToUse = existingGroupId || (isTestEnvironment() ? 'test-group-id' : null);
    if (!groupIdToUse) {
      console.log('No group ID available, skipping API update for field:', field);
      return;
    }
    
    // Get the current facility from the `facilities` state (which is driven by react-query)
    const facilityToUpdate = facilities[facilityIndex]; 
    if (!facilityToUpdate) {
        console.error('Facility to update not found at index:', facilityIndex);
        return;
    }
        
    const facilityId = facilityToUpdate.id || facilityToUpdate._id;

    if (facilityId) {
      const debouncedUpdate = getDebouncedFieldUpdate(
        `facility-${facilityId}-${field}`, // More specific key
        field, // Field name is part of the key now, not passed as arg to callback
        async (latestValue) => { // latestValue is the value for 'field'
          updateFacilityFieldMutation.mutate({
            groupId: groupIdToUse,
            facilityId,
            field,
            value: latestValue,
            facilityIndex // Pass for error reporting if needed
          });
        },
        800
      );
      debouncedUpdate(value);
    } else {
      // Facility doesn't exist on server yet (no ID). Debounce creation.
      const creationKey = `facility_creation_debounced_${facilityIndex}`;
      if (isPending(creationKey)) {
        console.log('Skipping duplicate facility creation, operation already pending');
        return;
      }
      markAsPending(creationKey);

      const debouncedCreateWithField = getDebouncedFieldUpdate(
        creationKey,
        'create-with-field',
        async (latestValueForField) => {
          try {
            const newFacilityData = { ...createEmptyFacility(), ...facilityToUpdate }; // Start with current local state

            // Apply the specific field update that triggered this creation
            if (field.includes('.')) {
              const parts = field.split('.');
              let current = newFacilityData;
              for (let i = 0; i < parts.length - 1; i++) {
                if (!current[parts[i]]) current[parts[i]] = {};
                current = current[parts[i]];
              }
              current[parts[parts.length - 1]] = latestValueForField;
            } else {
              newFacilityData[field] = latestValueForField;
            }
            
            const transformedFacility = transformFacilityForApi(newFacilityData);
            createFacilityMutation(
              { groupId: groupIdToUse, data: transformedFacility },
              {
                onSuccess: (response) => {
                  clearPending(creationKey);
                  console.log('Facility created via debounced field update:', response);
                  // Query invalidation in createFacilityMutation will handle data sync.
                },
                onError: (error) => {
                  clearPending(creationKey);
                  console.error('Error creating facility via debounced field update:', error);
                  setErrors(prev => ({ 
                    ...prev, 
                    [`facility_${facilityIndex}_create_field`]: `Failed to create facility with ${field}.`
                  }));
                }
              }
            );
          } finally {
            // clearPending(creationKey); // Already cleared in onSuccess/onError
          }
        },
        1200 // Longer debounce for creation
      );
      debouncedCreateWithField(value); // Pass the initial field's value
    }
  }, [
      existingGroupId, 
      facilities, 
      updateFacilityFieldMutation, 
      createFacilityMutation, 
      setErrors
    ]
  );

  const removeFacility = useCallback((facilityId, facilityIndex) => {
    if (!existingGroupId) {
      // If no existing group, just remove from local state
      setFacilities(prev => prev.filter((_, idx) => idx !== facilityIndex));
      if (previewFacilityIndex >= facilityIndex) {
        setPreviewFacilityIndex(Math.max(0, previewFacilityIndex -1));
      }
      setShowDelFacModal(false);
      return;
    }
    // If group exists, call server
    setDeletionInProgress(true);
    deleteFacilityMutation(
      { groupId: existingGroupId, facilityId },
      {
        onSuccess: () => {
          // Query invalidation in deleteFacilityMutation handles data sync.
          // Adjust previewFacilityIndex if needed after data syncs and facilities array updates.
          if (previewFacilityIndex >= facilityIndex && facilities.length > 1) {
             setPreviewFacilityIndex(Math.max(0, previewFacilityIndex -1));
          } else if (facilities.length <=1) {
             setPreviewFacilityIndex(0);
          }
          console.log('Facility deletion successful, UI will update via query refetch.');
        },
        // onError is handled globally by the hook
      }
    );
  }, [existingGroupId, deleteFacilityMutation, previewFacilityIndex, facilities.length]); // Added facilities.length

  const handleConfirmDeleteFacility = useCallback(() => {
    if (facToRemove) {
      removeFacility(facToRemove.id, facToRemove.index);
    }
  }, [facToRemove, removeFacility]);

  const openDeleteFacilityModal = useCallback((facilityId, facilityIndex) => {
    setFacToRemove({ id: facilityId, index: facilityIndex });
    setShowDelFacModal(true);
  }, []);

  const closeDeleteFacilityModal = useCallback(() => {
    setShowDelFacModal(false);
    setFacToRemove(null);
  }, []);

  const contextValue = {
    facilities, // This is the local state, driven by react-query
    setFacilities, // Should be used sparingly, prefer mutations
    previewFacilityIndex,
    setPreviewFacilityIndex,
    addFacility,
    updateFacilityField,
    removeFacility, // Expose the main removeFacility
    
    // Mutations and their states
    createFacility: createFacilityMutation, 
    isCreatingFacility,
    updateFullFacility: updateFullFacilityMutation,
    isUpdatingFullFacility,
    updateFacilityFieldOptimistic: updateFacilityFieldMutation, // Expose new mutation
    isUpdatingField: updateFacilityFieldMutation.isLoading, // Expose loading state
    deleteFacility: deleteFacilityMutation,
    isDeletingFacility,
    
    // Query state
    isFetchingFacilities,
    refetchFacilities,
    
    // Modal controls
    showDelFacModal,
    openDeleteFacilityModal,
    closeDeleteFacilityModal,
    handleConfirmDeleteFacility,
    deletionInProgress,

    // For components to directly access react-query data if needed (though `facilities` state is preferred)
    facilitiesDataFromQuery 
  };

  return (
    <FacilityContext.Provider value={contextValue}>
      {children}
    </FacilityContext.Provider>
  );
};

export const useFacility = () => useContext(FacilityContext);
